import {
  getCurrentInstance,
  reactive,
  ref,
  computed,
  watchEffect,
  watch,
} from "vue";
import asyncOperations
  from "@/client/extensions/composition/asyncOperations.js";

import startsWith from 'lodash/startsWith';

const adapters = {
  saffron: class SaffronMediaManagerAdaptor {
    asyncOps = null;

    constructor (options = {}) {
      this.asyncOps = asyncOperations({}).asyncOps;
      return this;
    }

    getAdaptorType () {
      console.log("test media manager - saffron adaptor");
      return "saffron";
    }

    async _getUploadToken (file) {
      // get token
      const path        = config.mediaManager.saffron.getTokenRelativeUrl;
      const payload     = { action: "create" };
      const tokenResult = await this.asyncOps.asyncCall(path, payload, {
        method: "post",
      });

      if (tokenResult.isError) {
        return false;
      }

      let { token, accountUUID } = tokenResult.data;
      return { token, accountUUID };
    }

    async _uploadFileToFileServer (accountUUID, token, file) {
      const uploadPath    = config.mediaManager.saffron.uploadFilePath;
      const uploadData    = {
        accountUUID,
        token,
        file,
      };
      const uploadOptions = {
        asFormData    : true,
        method        : "post",
        requestUrlFull: true,
      };
      const uploadResult  = await this.asyncOps.asyncCall(
        uploadPath,
        uploadData,
        uploadOptions,
      );
      if (uploadResult.isError) {
        return false;
      }

      return uploadResult.data.fileName;
    }

    async _createMediaByHandleAndFile (handle, file, data = {}) {
      let createMediaUrl     = config.mediaManager.saffron.baseRelativeUrl;
      let createMediaPayload = {
        fileName: file.name.replace(/[^a-zA-Z0-9-_/.]/g, "_").
          replace(/_{2,}/g, "_"),
        mimeType: file.type,
        type    : file.type.split("/").shift(),
        handler : "saffron",
        handle  : handle,
        ...data,
      };

      const createResult = await this.asyncOps.asyncCall(
        createMediaUrl,
        createMediaPayload,
        { method: "post" },
      );

      if (createResult.isError) {
        return false;
      }

    return createResult.data;
    }

    async upload (file, options = {data: {}}) {

      // get token
      let data = options.data ? options.data : {};
      const tokenResult = await this._getUploadToken(file);

      if ( ! tokenResult) {
        return false;
      }

      let { token, accountUUID } = tokenResult;

      const handle = await this._uploadFileToFileServer(
        accountUUID,
        token,
        file,
      );

      if ( ! handle) {
        return false;
      }

      const createResult = await this._createMediaByHandleAndFile(handle, file,      data);

      if ( ! createResult) {
        return false;
      }

      return createResult;
    }

    async rename (id, newName) {
      let updateMediaUrl     =
            config.mediaManager.saffron.baseRelativeUrl + "/" + id;
      let updateMediaPayload = {
        fileName: newName,
      };

      const updateResult = await this.asyncOps.asyncCall(
        updateMediaUrl,
        updateMediaPayload,
        { method: "patch" },
      );

      if (updateResult.isError) {
        return false;
      }

      return updateResult.data;
    }

    async delete (id) {
      let updateMediaUrl     =
            config.mediaManager.saffron.baseRelativeUrl + "/" + id;
      let updateMediaPayload = {};

      const updateResult = await this.asyncOps.asyncCall(
        updateMediaUrl,
        updateMediaPayload,
        { method: "delete" },
      );

      if (updateResult.isError) {
        return false;
      }

      return true;
    }
  },
};

const implementsAdapterInterface = (candidate) => {
  if (typeof candidate !== "object" || ! candidate) {
    return false;
  }
  const functions = ["getAdaptorType", "upload", "rename", "delete"];

  for (const functionName of functions) {
    if (typeof candidate[functionName] !== "function") {
      return false;
    }
  }

  return true;
};

export default (props, options = {}) => {
  let adapterName = options.adapter
    ? options.adapter
    : config.mediaManager.adapter;

  if ( ! adapters[adapterName]) {
    warn("Can not find adapter for media manager, check your config", {
      adapterName,
      props,
      options,
    });
  }

  let adaptor = new adapters[adapterName](options);

  if ( ! implementsAdapterInterface(adaptor)) {
    warn(
      "adaptor does not meet interface requirements, check your code (media manager)",
    );
  }

  ///////////////

  const asyncOps = asyncOperations({}).asyncOps;

  const pageSize = 50;

  const defaultListSettings = {
    filters   : {},
    pagination: {
      start: 0,
      limit: pageSize,
    },
    ordering  : {
      key      : "createdAt",
      direction: "desc",
    },
  };

  const listDefinition      = reactive({
    ...defaultListSettings,
  });

  const resetList = () => {
    listDefinition.filters    = defaultListSettings.filters;
    listDefinition.pagination = defaultListSettings.pagination;
    listDefinition.ordering   = defaultListSettings.ordering;
  };

  const items = ref([]);

  const folders = ref([]);

  const totalItems = ref(0);

  const isLoadingItems = ref(false);

  const hasItems = computed(() => items.value.length > 0);

  const hasError = ref(false);

  const refreshListItems = async () => {
    isLoadingItems.value = true;
    const result         = await asyncOps.asyncCall("media-manager", listDefinition);

    if (result.isError) {
      hasError.value        = true;
      items.value          = [];
      isLoadingItems.value = false;
    }

    items.value          = result.data.items;
    folders.value          = result.data.folders;
    totalItems.value = Number(result.data.totalCount);
    hasError.value        = false;
    isLoadingItems.value = false;

    return result;
  };

  watch(listDefinition,
    (newValue,oldValue) => {
      refreshListItems();
    },
    { immediate: false, deep: true },
  );

  const setFilter = (name, value) => {
    listDefinition.filters[name] = value;
    setLimit(pageSize);
    return true;
  };

  const setFolder = (value) => {
    listDefinition.filters['folder'] = value;
    setLimit(pageSize);
    return true;
  };

  const unsetFilter = (name) => {
    if (listDefinition.filters.hasOwnProperty(name)) {
      delete listDefinition.filters[name];
      setLimit(pageSize);
      return true;
    }
  };

  const setLimit = (limit) => {
    listDefinition.pagination.limit = Number(limit);
  }

  const loadMore = () => {
    listDefinition.pagination.limit = listDefinition.pagination.limit + pageSize;
  }

  const isImage = (file) => {
    try {
      return file.type.startsWith("image/");
    } catch(e) {
      warn('useMediaManager.isImage - returning false - error, file argument is probably invalid', file);
      return false;
    }
  };

  const isResizableImage = (file) => {
    try {
      let resizableMimeTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/bmp", "image/tiff", ]
      return resizableMimeTypes.includes(file.type);
    } catch(e) {
      warn('useMediaManager.isResizableImage - returning false - error, file argument is probably invalid', file);
      return false;
    }
  };

  const  resizeImage = async (file, maxWidth = 1920, maxHeight = 1024) => {
   if (! isResizableImage(file)) {
     return file;
   }

    if (utilities.isSSR()) {
      return file;
    }
    let canvasElement = document.createElement('canvas');

    let resizeOnCanvas = (img) => {
      let { width, height } = img;

      if (width > height) {
        if (width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }
      }

      // Create a canvas element
      const ctx = canvasElement.getContext("2d");

      // Resize the canvas to the new dimensions
      canvasElement.width = width;
      canvasElement.height = height;

      // Draw the image on the canvas
      ctx.drawImage(img, 0, 0, width, height);
      return true;
    };
    let blobToFile = (blob) => {
      return new File([blob], file.name, {
        type: file.type,
        lastModified: Date.now(),
      });
    };
    let imageLoadHandler = (img, resolve) => {
      resizeOnCanvas(img);
      // Convert the canvas content to a Blob object
      canvasElement.toBlob((blob) => {
        resolve(blobToFile(blob));
      }, file.type);
    };

    let result = await new Promise((resolve) => {
      let img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => imageLoadHandler(img, resolve);
    });
    // cause garbage collector to destroy the canvas
    canvasElement = null;
    return result;
  };
  // list behaviour

  const getFolderName = (path) => {
    if (typeof path !== 'string') {
      return false;
    }

    let split = path.split('/');
    let key = split.length - 1;

    let res = split[key];
    if (! res || res === '') {
      return '';
    }
    return split[key];
  }

  const getParentFolder = (path) => {
    if (typeof path !== 'string') {
      return false;
    }
    let split = path.split('/');
    delete split[split.length - 1];
    let join = split.join('/');

    if ( ! join || join === '') {
      return false;
    }

    if (join === '/') {
      return  '/';
    }
    if (join.endsWith('/')){
      return join.replace(/\/+$/, '');
    }
    return false;
  }


  const isFolderPathNested = (childPath,parentPath) => {
    return startsWith(childPath, parentPath);
  }


  return {
    items,
    folders,
    totalItems,
    hasItems,
    isLoadingItems,
    hasError,
    upload: adaptor.upload.bind(adaptor),
    rename: adaptor.rename.bind(adaptor),
    delete: adaptor.delete.bind(adaptor),
    resetList,
    refreshListItems,
    loadItems: refreshListItems,
    reloadItems: refreshListItems,
    reload: refreshListItems,
    setFolder,
    setFilter,
    unsetFilter,
    setLimit,
    loadMore,
    resizeImage,
    isImage,
    getFolderName,
    getParentFolder,
    isFolderPathNested,
  };
};
